import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import styles from './Chat.module.css';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { ChatInput } from './ChatInput';
import { MessageProps } from './types';
import { CustomerContext } from 'contexts/CustomerContext';
import { Message } from './Message';
import { ReduxState } from 'ducks';
import { loadConversationId, createChat, fetchChats } from 'ducks/client/chats';
import { AnimatedClickableDiv } from 'components/Map/AnimatedClickableDiv';
import { Loader } from './Loader';
import { useCurrentPosition } from 'hooks/useCurrentPosition';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { useChatContext } from 'components/Chat/ChatContext';
import { WelcomeMessage } from './WelcomeMessage';
import { WelcomeMessage as WelcomeMessageType } from 'components/Chat/ChatContext';
import { useWelcomeMessage } from './util';

interface ChatWindowProps {
  digitalMapId: string;
  onClose?: () => void;
  className?: string;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ digitalMapId, onClose, className }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const chatInputRef = React.useRef<HTMLTextAreaElement>(null);
  const messageRefs = React.useRef<
    {
      el: HTMLDivElement;
      isUser: boolean;
    }[]
  >([]);
  const [welcomeMessageFromHistory, setWelcomeMessageFromHistory] = React.useState<
    (WelcomeMessageType & { selectedButton: string }) | null
  >(null);
  const [messages, setMessages] = React.useState<MessageProps[]>([]);
  const [conversationId, setConversationId] = React.useState<string>('');
  const chatWindowRef = React.useRef<HTMLDivElement>(null);
  const chatContext = useChatContext();

  const all = useSelector((state: ReduxState) => state.chats.all);
  const isLoading = useSelector((state: ReduxState) => state.chats.inFlight);
  const inFlightMessage = useSelector((state: ReduxState) => state.chats.inFlightMessage);
  const digitalMap = useSelector(selectDigitalMap);
  const { apiKey } = React.useContext(ApiKeyContext);
  const { customer } = React.useContext(CustomerContext);
  const { currentPositionRef } = useCurrentPosition();
  const welcomeMessage = useWelcomeMessage();

  const handleSendMessage = (inputMessage: string): void => {
    if (inputMessage.trim() !== '') {
      dispatch(
        createChat(
          apiKey,
          {
            conversation_id: conversationId,
            question: inputMessage,
            digital_map_id: digitalMapId,
            customer_id: customer?.id,
            location: currentPositionRef.current
              ? {
                  lat: currentPositionRef.current.lat(),
                  lng: currentPositionRef.current.lng(),
                }
              : undefined,
            welcome_message_text: chatContext?.action?.welcomeMessage?.text,
            button_options: chatContext?.action?.welcomeMessage?.buttons,
            selected_button: chatContext?.action?.selectedButton,
          },
          i18n.language
        )
      );
    }
  };

  React.useEffect(() => {
    const conversationId = loadConversationId(digitalMapId);
    setConversationId(conversationId);
    dispatch(fetchChats(apiKey, conversationId, i18n.language));
  }, [apiKey, digitalMapId, i18n.language, dispatch]);

  React.useEffect(() => {
    const messages: MessageProps[] = [];

    all?.forEach((chat) => {
      if (chat.welcome_message_text) {
        setWelcomeMessageFromHistory({
          text: chat.welcome_message_text,
          buttons: chat.button_options ?? [],
          selectedButton: chat.selected_button ?? '',
        });
      }
      messages.push({ text: chat.question, isUser: true });
      messages.push({ text: chat.answer, isUser: false });
    });

    if (inFlightMessage) {
      messages.push({ text: inFlightMessage, isUser: true });
    }

    setMessages(messages);
  }, [t, isLoading, all, digitalMap, currentPositionRef, inFlightMessage]);

  React.useLayoutEffect(() => {
    // Whenever messages or loading state changes, scroll the most recent user message into view
    const userMessage = messageRefs.current.findLast((ref) => ref.isUser);
    if (userMessage) {
      userMessage.el?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  return (
    <div className={clsx(styles.chatModal, className)} ref={chatWindowRef}>
      {onClose && (
        <div className={styles.header}>
          <AnimatedClickableDiv
            className={styles.closeButton}
            clickedClassName={styles.clicked}
            onClick={onClose}
          >
            <img src="/static/images/map/close.svg" className={styles.closeIcon} alt="close" />
          </AnimatedClickableDiv>
        </div>
      )}
      <div className={styles.messagesList}>
        <div className={styles.logoContainer}>
          <img src="/static/images/map/nutmeg-ai-logo.svg" className={styles.logo} alt="logo" />
        </div>
        {welcomeMessageFromHistory ? (
          <WelcomeMessage
            className={styles.welcomeMessage}
            message={welcomeMessageFromHistory.text}
            buttons={welcomeMessageFromHistory.buttons}
            selectedButton={welcomeMessageFromHistory.selectedButton}
          />
        ) : welcomeMessage && messages.length <= 1 ? (
          <WelcomeMessage
            className={styles.welcomeMessage}
            message={chatContext?.action?.welcomeMessage?.text ?? welcomeMessage.message ?? ''}
            buttons={chatContext?.action?.welcomeMessage?.buttons ?? welcomeMessage.buttons ?? []}
            selectedButton={chatContext?.action?.selectedButton ?? ''}
            onButtonClick={(welcomeMessage, button) => {
              chatContext?.setAction({
                welcomeMessage,
                selectedButton: button.label,
              });
            }}
          />
        ) : null}
        {messages.map((message, index) => (
          <Message
            key={index}
            text={message.text}
            isUser={message.isUser ?? false}
            onCloseChat={onClose ?? (() => {})}
            ref={(el) => {
              messageRefs.current[index] = {
                el: el as HTMLDivElement,
                isUser: message.isUser ?? false,
              };
            }}
          />
        ))}
        {isLoading && (
          <div className={styles.loadingArea}>
            <Loader />
          </div>
        )}
      </div>
      <ChatInput onSendMessage={handleSendMessage} ref={chatInputRef} />
    </div>
  );
};
